import { DateTime } from "luxon";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import { getPreferredName } from "@/lib/redacted-display";
import { isMagicMomentBannerExpired } from "@/lib/utils/magic-moments";

// Priority order: reactivation > momentum > day1 > day2.
export type AttentionBannerKind = "reactivation" | "momentum" | "day1" | "day2";

export type AttentionBannerActionIcon =
  | "ice_cream"
  | "heart"
  | "shield"
  | "sparkles"
  | "swap"
  | "share"
  | "tips"
  | "hot_takes";

export type AttentionBannerLaunchTarget =
  | "action_panel"
  | "create_swap"
  | "create_moment";

export interface AttentionBannerTip {
  eyebrow: string;
  body: string;
}

type AttentionBannerActionBase = {
  id: string;
  label: string;
  icon?: AttentionBannerActionIcon;
};

export type AttentionBannerAction =
  | (AttentionBannerActionBase & {
      kind: "launch";
      target: AttentionBannerLaunchTarget;
    })
  | (AttentionBannerActionBase & {
      kind: "tip";
      body: string;
    })
  | (AttentionBannerActionBase & {
      kind: "tips";
      tips: readonly AttentionBannerTip[];
    })
  | (AttentionBannerActionBase & {
      kind: "magic_moment";
    });

export interface AttentionBannerDescriptor {
  kind: AttentionBannerKind;
  title: string;
  subtitle: string;
  actions: AttentionBannerAction[];
  teenGender?: "male" | "female" | null;
}

export interface AttentionBannerSignals {
  dayState: "day1" | "day2" | null;
  day1Enabled: boolean;
  day2Enabled: boolean;
  day1Dismissed: boolean;
  day2Dismissed: boolean;
  day2Expired: boolean;
  reactivationActive: boolean;
  reactivationDismissed: boolean;
  momentumActive: boolean;
  teenName: string | null;
  teenGender: "male" | "female" | null;
}

export function isInlineBannerAction(
  action: AttentionBannerAction,
): action is Exclude<AttentionBannerAction, { kind: "launch" }> {
  return action.kind !== "launch";
}

const DAY1_ACTIONS: AttentionBannerAction[] = [
  {
    id: "break_the_ice",
    label: "Break the ice",
    icon: "ice_cream",
    kind: "tip",
    body: "Share something about yourself, or ask what they're into. The goal is just to get the convo flowing!",
  },
  {
    id: "be_human",
    label: "Be human",
    icon: "heart",
    kind: "tip",
    body: "Send a voice note or video so they know there's a human on the other end.",
  },
  {
    id: "show_support",
    label: "Show your support",
    icon: "shield",
    kind: "tip",
    body: "Let them know you're there for whatever they need — venting, advice, hype, or just someone to listen.",
  },
];

const DAY2_TIPS: readonly AttentionBannerTip[] = [
  {
    eyebrow: "Break the ice",
    body: "Ask something you're actually curious about. Teens can tell the difference between a real question and a checklist.",
  },
  {
    eyebrow: "Go a little deeper",
    body: "Ask what they'd do with a completely free Saturday. It tells you what they actually care about.",
  },
  {
    eyebrow: "Keep it playful",
    body: "Send a hot take — pineapple on pizza, the best cereal, the worst movie everyone pretends to love. Low-stakes debates get people talking fast.",
  },
];

const DAY2_ACTIONS: AttentionBannerAction[] = [
  {
    id: "magic_moment",
    label: "Magic moment",
    icon: "sparkles",
    kind: "magic_moment",
  },
  {
    id: "start_swap",
    label: "Start a Swap",
    icon: "swap",
    kind: "launch",
    target: "create_swap",
  },
  {
    id: "share_moment",
    label: "Share a moment",
    icon: "share",
    kind: "launch",
    target: "create_moment",
  },
  { id: "tips", label: "Tips", icon: "tips", kind: "tips", tips: DAY2_TIPS },
];

const MOMENTUM_ACTIONS: AttentionBannerAction[] = [
  {
    id: "hot_takes",
    label: "Hot takes",
    icon: "hot_takes",
    kind: "launch",
    target: "action_panel",
  },
  {
    id: "magic_moment",
    label: "Magic moment",
    icon: "sparkles",
    kind: "launch",
    target: "action_panel",
  },
  {
    id: "start_swap",
    label: "Start a Swap",
    icon: "swap",
    kind: "launch",
    target: "create_swap",
  },
];

function teenLabel(teenName: string | null): string {
  return teenName || "your teen";
}

/** Highest-priority active banner for the selected teen, or `null` when none. */
export function resolveAttentionBanner(
  signals: AttentionBannerSignals,
): AttentionBannerDescriptor | null {
  if (signals.reactivationActive && !signals.reactivationDismissed) {
    return {
      kind: "reactivation",
      title: `${teenLabel(signals.teenName)} just reactivated!`,
      subtitle: "Take some extra time to send a thoughtful message today.",
      actions: [],
    };
  }

  if (signals.momentumActive) {
    return {
      kind: "momentum",
      title: "Momentum alert",
      subtitle: `Based on their first few days, ${teenLabel(
        signals.teenName,
      )} is at risk of disengaging. Reach out today with something personal to give your relationship some momentum!`,
      actions: MOMENTUM_ACTIONS,
    };
  }

  if (
    signals.dayState === "day1" &&
    signals.day1Enabled &&
    !signals.day1Dismissed
  ) {
    return {
      kind: "day1",
      title: "Day 1",
      subtitle:
        "Day 1 sets the tone! If you're not sure where to start, tap below for some ideas.",
      actions: DAY1_ACTIONS,
    };
  }

  if (
    signals.dayState === "day2" &&
    signals.day2Enabled &&
    !signals.day2Dismissed &&
    !signals.day2Expired
  ) {
    return {
      kind: "day2",
      title: "Day 2",
      subtitle:
        "This is an important moment to slow down and be thoughtful. The tools below are here if you need them.",
      actions: DAY2_ACTIONS,
      teenGender: signals.teenGender,
    };
  }

  return null;
}

export interface BuildAttentionBannerSignalsOptions {
  day1Enabled: boolean;
  day2Enabled: boolean;
}

/**
 * Map a mentorship payload to normalized signals, including persisted
 * `*_banner_dismissed` flags from mentorship metadata.
 */
export function buildAttentionBannerSignals(
  mentorship: MentorshipDetails | null | undefined,
  options: BuildAttentionBannerSignalsOptions,
  now: DateTime = DateTime.now(),
): AttentionBannerSignals {
  const magicMoment = mentorship?.metadata?.magic_moment;
  const reactivation = mentorship?.metadata?.reactivation;
  const momentum = mentorship?.metadata?.at_risk_momentum;
  const dayState = magicMoment?.day_state ?? null;

  return {
    dayState,
    day1Enabled: options.day1Enabled,
    day2Enabled: options.day2Enabled,
    day1Dismissed: Boolean(magicMoment?.day1_banner_dismissed),
    day2Dismissed: Boolean(magicMoment?.day2_banner_dismissed),
    day2Expired: isMagicMomentBannerExpired(
      dayState ?? undefined,
      magicMoment?.expires_at,
      now,
    ),
    reactivationActive: Boolean(reactivation),
    reactivationDismissed: reactivation?.banner_dismissed === true,
    momentumActive: momentum?.status === "active",
    teenName: mentorship?.mentee
      ? getPreferredName(mentorship.mentee) || null
      : null,
    teenGender: magicMoment?.teen_gender ?? null,
  };
}
